import { Component, DestroyRef, inject, signal, Type } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormArray } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { AppsForKnowledgeSourceComponent } from '../../../apps-for-knowledge-source/apps-for-knowledge-source.component';
import { AiKnowledgeService } from '../../../ai-knowledge.service';
import {
  KnowledgeSource,
  KnowledgeSourceConfig,
  KnowledgeSourceConfigWebsiteScrapeConfig,
  WebsiteScrapeConfigProgress,
} from '@vendasta/embeddings';
import { CommonKnowledgeSourceEditorComponent } from '../../common/common-knowledge-source-editor.component';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { defer, from, map, retry, take } from 'rxjs';
import { AiKnowledgeI18nModule } from '../../../assets/i18n/ai-knowledge-i18n.module';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebsiteScrapeComponent } from '../website-scrape.component';

export const EDIT_OPTION = 'scrapedPages';

@Component({
  selector: 'ai-knowledge-scraped-pages',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    AppsForKnowledgeSourceComponent,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './scraped-pages.component.html',
  styleUrls: ['./scraped-pages.component.scss'],
})
export class ScrapedPagesComponent extends CommonKnowledgeSourceEditorComponent {
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly snackbarService = inject(SnackbarService);
  private destroyRef = inject(DestroyRef);
  websiteScrapeConfig: KnowledgeSourceConfigWebsiteScrapeConfig;
  scrapeProgressStates = WebsiteScrapeConfigProgress;
  form: FormGroup | undefined;
  readonly isLoading = signal(false);
  disabled = true;

  init(): void {
    this.disableSubmit = true;
    this.websiteScrapeConfig = this.knowledgeSource?.config?.websiteScrapeConfig;
    this.form = new FormGroup({
      scrapedPages: new FormArray([]),
    });

    if (this.knowledgeSource) {
      this.isLoading.set(true);
      defer(() =>
        from(this.knowledgeService.getKnowledgeSource(this.knowledgeSource?.id, this.knowledgeSource?.namespace)),
      )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((ks) => {
            this.updateKnowledgeSource(ks);
            this.isLoading.set(false);
            if (
              this.websiteScrapeConfig?.scrapeProgress !==
              WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE
            ) {
              // throw error to poll again
              throw false;
            }
            this.disableSubmit = false;
          }),
          retry({ delay: 5000 }),
          take(1),
        )
        .subscribe();
    }
  }

  updateKnowledgeSource(ks: KnowledgeSource): void {
    this.knowledgeSource = ks;
    this.websiteScrapeConfig = this.knowledgeSource?.config?.websiteScrapeConfig;
    if (this.websiteScrapeConfig.scrapeProgress === WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE) {
      this.websiteScrapeConfig.scrapedPages.sort((a, b) =>
        a?.trainingEnabled === b?.trainingEnabled ? a?.url?.localeCompare(b?.url) : a?.trainingEnabled ? -1 : 1,
      );
      this.form.controls.scrapedPages = new FormArray(
        this.websiteScrapeConfig.scrapedPages.map((page) => new FormControl(page.trainingEnabled)),
      );
      this.form.controls.scrapedPages.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(() => this.form.markAsDirty()),
        )
        .subscribe();
    }
  }

  get scrapedPagesArray(): UntypedFormArray {
    return this.form.controls['scrapedPages'] as UntypedFormArray;
  }

  selectAll(): void {
    this.form.controls.scrapedPages = new FormArray(
      this.websiteScrapeConfig.scrapedPages.map(() => new FormControl(true)),
    );
  }

  getScrapedPagesCount(): number {
    return this.websiteScrapeConfig?.scrapedPages?.length || 0;
  }

  async submit(): Promise<string> {
    this.scrapedPagesArray.controls.forEach((pageControl, index) => {
      this.websiteScrapeConfig.scrapedPages[index].trainingEnabled = pageControl.value;
    });
    const config = {
      websiteScrapeConfig: this.websiteScrapeConfig,
    } as KnowledgeSourceConfig;
    if (this.knowledgeSource) {
      const source = this.knowledgeSource;
      source.config = config;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(source);
      return source.id;
    }
    this.snackbarService.openErrorSnack('COMMON.ERROR_OCCURRED');
    return;
  }

  getScrapeProgressTitle(): string {
    if (
      this.websiteScrapeConfig?.scrapeProgress === WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_QUEUED
    ) {
      return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SCRAPE_QUEUED';
    }
    return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SCRAPE_IN_PROGRESS';
  }

  previousStep(): Type<CommonKnowledgeSourceEditorComponent> {
    return WebsiteScrapeComponent;
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  getEditOption(): string {
    return EDIT_OPTION;
  }
}
